import { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { CheckCircle2, XCircle, AlertTriangle, ScanLine, Loader2, RefreshCw, ArrowLeft } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { toast } from "sonner";

// Standalone scanner page for staff via code: /scan/:code
export function StaffScanner() {
  const { code } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/public/scanners/${code}`).then((r) => setInfo(r.data)).catch((e) => setError(formatApiError(e)));
  }, [code]);

  if (error) return (
    <div className="container-mc py-24 text-center" data-testid="scanner-invalid">
      <XCircle className="w-14 h-14 mx-auto text-destructive" />
      <h1 className="font-display font-black text-2xl mt-4">{error}</h1>
      <p className="text-sm text-muted-foreground mt-2">Demandez à l'artiste un nouveau code contrôleur.</p>
    </div>
  );
  if (!info) return <div className="py-24 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin" /></div>;

  return <ScannerUI event={info.event} scannerCode={code.toUpperCase()} label={info.scanner.label} />;
}

// Artist scanner (logged in): /artist/scan?event=<id>
export function ArtistScannerPage() {
  const [params] = useSearchParams();
  const eventId = params.get("event");
  const { user } = useAuth();
  const [event, setEvent] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (eventId) api.get(`/events/${eventId}/manage`).then((r) => setEvent(r.data.event)).catch((e) => setError(formatApiError(e)));
  }, [eventId]);

  if (!user || user.role !== "artist") return <div className="container-mc py-24 text-center">Réservé aux artistes.</div>;
  if (error) return (
    <div className="container-mc py-24 text-center" data-testid="artist-scanner-error">
      <XCircle className="w-14 h-14 mx-auto text-destructive" />
      <h1 className="font-display font-black text-2xl mt-4">{error}</h1>
      <Link to="/artist/events" className="btn-primary inline-flex mt-6">Retour à mes événements</Link>
    </div>
  );
  if (!event) return <div className="py-24 text-center"><Loader2 className="w-8 h-8 mx-auto animate-spin" /></div>;
  return <ScannerUI event={event} scannerCode={null} label={`${user.name} (artiste)`} backTo={`/artist/events/${eventId}`} />;
}

function cameraErrorMessage(e) {
  const name = e?.name || "";
  const msg = String(e?.message || e || "");
  if (!window.isSecureContext) return "La caméra nécessite une connexion sécurisée (https). Ouvrez le site avec l'adresse https://…";
  if (name === "NotAllowedError" || name === "PermissionDeniedError" || /permission/i.test(msg)) {
    return "Accès caméra refusé. Autorisez la caméra pour ce site (icône 🔒 ou caméra dans la barre d'adresse), puis réessayez.";
  }
  if (name === "NotFoundError" || name === "OverconstrainedError" || /not found|no camera/i.test(msg)) {
    return "Aucune caméra utilisable détectée. Utilisez un téléphone ou un ordinateur avec webcam, ou saisissez le code du billet ci-dessous.";
  }
  if (name === "NotReadableError" || /in use|busy/i.test(msg)) {
    return "La caméra est utilisée par une autre application ou un autre onglet. Fermez-les et réessayez.";
  }
  return "Impossible d'ouvrir la caméra : " + msg + " — vous pouvez aussi saisir le code du billet ci-dessous.";
}

function ScannerUI({ event, scannerCode, label, backTo }) {
  const [scanning, setScanning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [camError, setCamError] = useState(null);
  const scannerRef = useRef(null);
  const containerId = "qr-scanner-region";
  const lastPayloadRef = useRef("");

  const start = async () => {
    setScanning(true);
    setLastResult(null);
    setCamError(null);
    try {
      const html5Qr = new Html5Qrcode(containerId);
      scannerRef.current = html5Qr;
      const config = { fps: 10, qrbox: { width: 260, height: 260 } };
      try {
        // 1er essai : caméra arrière (téléphones)
        await html5Qr.start({ facingMode: "environment" }, config, (decoded) => onScan(decoded), () => {});
      } catch (e1) {
        // 2e essai : n'importe quelle caméra disponible (PC portables, webcams)
        try {
          await html5Qr.start({ facingMode: "user" }, config, (decoded) => onScan(decoded), () => {});
        } catch (e2) {
          const cams = await Html5Qrcode.getCameras();
          if (!cams || cams.length === 0) throw e2;
          await html5Qr.start(cams[cams.length - 1].id, config, (decoded) => onScan(decoded), () => {});
        }
      }
    } catch (e) {
      const msg = cameraErrorMessage(e);
      setCamError(msg);
      toast.error(msg);
      setScanning(false);
      scannerRef.current = null;
    }
  };

  const stop = async () => {
    if (scannerRef.current) {
      try { await scannerRef.current.stop(); await scannerRef.current.clear(); } catch { /* ignore */ }
      scannerRef.current = null;
    }
    setScanning(false);
  };

  useEffect(() => () => { stop(); /* cleanup */ }, []);

  const onScan = async (payload) => {
    if (busy) return;
    if (payload === lastPayloadRef.current) return;
    lastPayloadRef.current = payload;
    setBusy(true);
    try {
      const { data } = await api.post("/tickets/scan", { qr_payload: payload, scanner_code: scannerCode });
      setLastResult({ ok: data.result === "ok", already: data.result === "already_used", ...data });
      if (data.result === "ok") toast.success("Billet validé ✓");
      else if (data.result === "already_used") toast.warning("Billet déjà utilisé");
    } catch (e) {
      setLastResult({ ok: false, error: formatApiError(e) });
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
      // allow rescan after 2s
      setTimeout(() => { lastPayloadRef.current = ""; }, 2000);
    }
  };

  const rescan = () => { setLastResult(null); lastPayloadRef.current = ""; };

  const submitManual = async (e) => {
    e.preventDefault();
    const code = manualCode.trim().toUpperCase();
    if (!code) return;
    setBusy(true);
    try {
      const { data } = await api.post("/tickets/scan", { qr_payload: code, scanner_code: scannerCode });
      setLastResult({ ok: data.result === "ok", already: data.result === "already_used", ...data });
      if (data.result === "ok") toast.success("Billet validé ✓");
      else if (data.result === "already_used") toast.warning("Billet déjà utilisé");
      setManualCode("");
    } catch (err) {
      setLastResult({ ok: false, error: formatApiError(err) });
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };

  return (
    <div className="container-mc py-8 max-w-lg" data-testid="scanner-page">
      {backTo && (
        <Link to={backTo} className="inline-flex items-center text-xs font-mono uppercase tracking-wider text-muted-foreground hover:text-foreground mb-4">
          <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Retour
        </Link>
      )}
      <div className="card-mc p-6">
        <div className="gn-strip h-1 -mt-6 -mx-6 mb-4" />
        <p className="overline mb-1">🎟️ Scanner d'entrée · {label}</p>
        <h1 className="font-display font-black text-2xl tracking-tighter leading-tight">{event.title}</h1>
        <p className="text-xs text-muted-foreground mt-1">{event.venue} · {new Date(event.event_date).toLocaleDateString("fr-FR")}</p>

        <div className="mt-6">
          {!scanning && !lastResult && (
            <button data-testid="start-scan-btn" onClick={start} className="btn-primary w-full text-lg py-4">
              <ScanLine className="w-5 h-5 mr-2" /> Ouvrir la caméra
            </button>
          )}
          {/* Le conteneur du scanner doit TOUJOURS être monté dans le DOM
              (Html5Qrcode le cherche par id au moment du clic) */}
          <div className={scanning ? "" : "hidden"}>
            <div id={containerId} className="w-full aspect-square border-2 border-foreground overflow-hidden bg-black" data-testid="qr-scanner-region" />
            <button data-testid="stop-scan-btn" onClick={stop} className="btn-outline w-full mt-3">Arrêter</button>
          </div>
          {camError && !scanning && (
            <div className="mt-3 p-3 border border-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5 flex gap-2 items-start" data-testid="scanner-camera-error">
              <AlertTriangle className="w-4 h-4 text-[hsl(var(--primary))] flex-shrink-0 mt-0.5" />
              <div className="text-xs">
                <p>{camError}</p>
                <button type="button" onClick={start} className="mt-2 underline font-bold" data-testid="scanner-camera-retry">Réessayer la caméra</button>
              </div>
            </div>
          )}
          {lastResult && (
            <ResultCard result={lastResult} onRescan={rescan} scanning={scanning} onResume={() => { rescan(); if (!scanning) start(); }} />
          )}

          <form onSubmit={submitManual} className="mt-5 border-t border-border pt-4" data-testid="manual-code-form">
            <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-2">Ou saisir le code billet (ex : BA-7XK2-9PMQ)</p>
            <div className="flex gap-2">
              <input data-testid="manual-code-input" value={manualCode} onChange={(e) => setManualCode(e.target.value.toUpperCase())}
                     placeholder="BA-XXXX-XXXX" className="flex-1 border border-border px-3 py-2 rounded-sm bg-card text-sm font-mono uppercase" />
              <button type="submit" data-testid="manual-code-submit" disabled={busy || !manualCode.trim()} className="btn-secondary text-sm px-4">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Vérifier"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function ResultCard({ result, onRescan, scanning, onResume }) {
  if (result.error) return (
    <div className="border-2 border-destructive p-6 text-center" data-testid="scan-error">
      <XCircle className="w-14 h-14 mx-auto text-destructive" />
      <h2 className="font-display font-black text-xl mt-3">Refusé</h2>
      <p className="text-sm text-muted-foreground mt-1">{result.error}</p>
      <button onClick={onResume} className="btn-primary mt-6 w-full" data-testid="rescan-btn"><RefreshCw className="w-4 h-4 mr-2" /> Scanner un autre billet</button>
    </div>
  );
  if (result.already) return (
    <div className="rounded-2xl border-2 border-yellow-500/60 p-6 text-center bg-yellow-500/10" data-testid="scan-already-used">
      <AlertTriangle className="w-14 h-14 mx-auto text-yellow-500" />
      <h2 className="font-display font-black text-xl mt-3">Billet déjà utilisé</h2>
      <p className="text-sm mt-1">{result.ticket?.buyer_name} · {result.ticket?.ticket_type_name}</p>
      {result.ticket?.code && <p className="font-mono text-xs font-bold mt-1" data-testid="scan-ticket-code">{result.ticket.code}</p>}
      <p className="text-xs text-muted-foreground mt-2">Scanné le {new Date(result.ticket?.scanned_at).toLocaleString("fr-FR")}</p>
      <button onClick={onResume} className="btn-primary mt-6 w-full" data-testid="rescan-btn"><RefreshCw className="w-4 h-4 mr-2" /> Scanner un autre billet</button>
    </div>
  );
  return (
    <div className="rounded-2xl border-2 border-[hsl(var(--accent))]/60 p-6 text-center bg-[hsl(var(--accent))]/10" data-testid="scan-ok">
      <CheckCircle2 className="w-14 h-14 mx-auto text-[hsl(var(--accent))]" />
      <h2 className="font-display font-black text-xl mt-3">Billet valide ✓</h2>
      <p className="text-lg font-bold mt-2">{result.ticket?.buyer_name}</p>
      <p className="text-sm text-muted-foreground">{result.ticket?.ticket_type_name}</p>
      {result.ticket?.code && <p className="font-mono text-xs font-bold mt-1" data-testid="scan-ticket-code">{result.ticket.code}</p>}
      <button onClick={onResume} className="btn-primary mt-6 w-full" data-testid="rescan-btn"><RefreshCw className="w-4 h-4 mr-2" /> Scanner un autre billet</button>
    </div>
  );
}
